/**
 * The Candidates list cannot ask for its rows until the app shell has loaded
 * the user, permissions and saved filters and then the list's lazy chunk —
 * locally that put the request ~2s in, and the request itself takes another
 * 1–3s on staging. Remembering the last first-page request lets the app fire
 * it during boot instead (`app/preloadContactsListRoute`), so it is usually
 * answered, or well under way, by the time the list mounts.
 *
 * Only a hint: the list still builds its own payload, and reuses the boot
 * request only when it serialises identically (see `consumeBootPrefetch`).
 */
const STORAGE_KEY = 'kula_contacts_list_last_request';

type ContactsPayload = {
  page?: number;
  scroll_token?: unknown;
  name?: string;
  [key: string]: unknown;
};

/**
 * Records a list request. Only a plain first page is kept — the one a fresh
 * visit starts on: later pages carry a scroll token, and a search is cleared
 * when the list unmounts, so neither would be asked for again.
 */
export const saveLastContactsRequest = (payload: ContactsPayload): void => {
  if (payload.page !== 1 || payload.scroll_token || payload.name) return;
  try {
    // JSON drops the undefined keys, exactly as RTK Query's serialiser does.
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or blocked: the list simply loads the usual way.
  }
};

/** The last first-page request, or null when there is none to replay. */
export const readLastContactsRequest = (): ContactsPayload | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ContactsPayload;
    return parsed && typeof parsed === 'object' && parsed.page === 1
      ? parsed
      : null;
  } catch {
    return null;
  }
};

// Key-order independent, and drops undefined like RTK Query's serialiser.
const argsKey = (value: unknown): string =>
  JSON.stringify(value, (_, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.keys(v)
            .sort()
            .map((k) => [k, v[k]])
        )
      : v
  );

let bootArgsKey: string | null = null;

/** Called by the boot preload with the payload it requested. */
export const markBootPrefetch = (payload: unknown): void => {
  bootArgsKey = argsKey(payload);
};

/**
 * True exactly once, for a payload matching the boot request — the list then
 * reads that (possibly still in-flight) entry instead of firing a duplicate.
 * Later fetches, and any visit after navigating away, go to the network.
 */
export const consumeBootPrefetch = (payload: unknown): boolean => {
  if (bootArgsKey === null) return false;
  const matches = bootArgsKey === argsKey(payload);
  bootArgsKey = null;
  return matches;
};
